import jsPDF from "jspdf";
import coverImg from "../assets/images/cover_horizontal.png";
import contentBgImg from "../assets/images/pages_horizontal.png";
import { captureDashboardBlocks } from "./captureDashboardBlocks";

function hexNoHash(hex) {
  const s = String(hex || "").trim();
  if (!s) return "FFFFFF";
  return s.startsWith("#") ? s.slice(1).toUpperCase() : s.toUpperCase();
}

async function urlToDataUrl(url, signal) {
  if (!url) return null;
  if (typeof url === "string" && url.startsWith("data:")) return url;

  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const blob = await res.blob();

    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function dataUrlType(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return "PNG";
  const m = dataUrl.match(/^data:image\/(png|jpeg|jpg);/i);
  const t = (m?.[1] || "png").toLowerCase();
  return t === "jpeg" || t === "jpg" ? "JPEG" : "PNG";
}

export async function exportDashboardPDF({
  filename = "relatorio",
  cover = coverImg,
  contentBg = contentBgImg,
  backgroundColor = "#ffffff",
  scale = 2,
  clientName = "Cliente",
  clientBrand,
  periodText = "Período",
  titleText = "Relatório de mídia",
  titles = null,
  titleColor = "#614a9e",
  coverTitleColor = "#614a9e",
  coverMetaColor = "#374151",
  slidePadding = 0.55,
  titleBoxH = 0.55,
  titleFontSize = 0.28, 
  titleLeftOffset = 0.95,
  tocColumns = 2,
  showPagination = true,
  pageNumberColor = "#6B7280",
  pageNumberSize = 0.15,
  selectedTitles = null,
  onProgress,
  signal,
} = {}) {
  const report = (progress, step) => {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (typeof onProgress === "function") onProgress({ progress, step });
  };

  report(1, "Preparando fundos…");
  const coverData = await urlToDataUrl(cover, signal);
  const contentBgData = await urlToDataUrl(contentBg);
  const brandData = await urlToDataUrl(clientBrand);

  report(6, "Capturando dashboard…");
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  const pages = await captureDashboardBlocks({
    selector: "#pdf-dashboard",
    scale,
    backgroundColor,
    captureConcurrency: 2,
    imageFormat: "PNG",
    selectedTitles,
    onProgress: ({ progress, step }) => report(Math.min(88, progress), step),
    signal,
  });

  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  report(90, "Montando PDF…");

  const PAGE_W = 13.333;
  const PAGE_H = 7.5;

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "in",
    format: [PAGE_W, PAGE_H],
    compress: true,
  });

  const FIRST_CONTENT_PAGE_NUMBER = 3; 
  const innerX = slidePadding - 0.35;
  const innerY = slidePadding;
  const innerW = PAGE_W - (innerX + slidePadding);
  const innerH = PAGE_H - slidePadding * 2;
  const CONTENT_IMAGE_LEFT_INSET = Math.max(0.6, titleLeftOffset - 0.2);
  const CONTENT_IMAGE_TOP_GAP = 0.1;
  const CONTENT_IMAGE_BOTTOM_GAP = 0.06;
  const CONTENT_IMAGE_MAX_SCALE_DEFAULT = 0.85;
  const CONTENT_IMAGE_MAX_SCALE_CIRCULAR = 0.70;
  const CONTENT_IMAGE_MAX_SCALE_WORDCLOUD = 0.60;

  function getContentImageMaxScale(layoutType) {
    if (layoutType === "circular") return CONTENT_IMAGE_MAX_SCALE_CIRCULAR;
    if (layoutType === "wordcloud") return CONTENT_IMAGE_MAX_SCALE_WORDCLOUD;
    return CONTENT_IMAGE_MAX_SCALE_DEFAULT;
  }

  function addBackground() {
    if (contentBgData) {
      pdf.addImage(contentBgData, "PNG", 0, 0, PAGE_W, PAGE_H);
      return;
    }
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, PAGE_W, PAGE_H, "F");
  }

  function addPagination(pageNumber, totalPages) {
    if (!showPagination) return;

    const txt = `${pageNumber}/${totalPages}`;
    const x = PAGE_W - slidePadding - 1.2;
    const y = PAGE_H - slidePadding - 0.12;

    pdf.setTextColor(pageNumberColor);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(pageNumberSize * 72); 
    pdf.text(txt, x + 1.2, y, { align: "right" });
  }

  function setTitleStyle() {
    pdf.setTextColor(titleColor);
    pdf.setFont("helvetica", "bold"); 
    pdf.setFontSize(titleFontSize * 72);
  }

  function setBodyStyle() {
    pdf.setTextColor("#111827");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(0.17 * 72);
  }

  {
    if (coverData) {
      pdf.addImage(coverData, "PNG", 0, 0, PAGE_W, PAGE_H);
    } else {
      // fallback
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, PAGE_W, PAGE_H, "F");
    }

    if (brandData) {
      const imgType = dataUrlType(brandData);

      const marginX = 0.80;
      const marginY = 0.20;

      const maxW = 2.2;
      const maxH = 0.75;

      const ratio = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width / img.height);
        img.onerror = () => resolve(maxW / maxH);
        img.src = brandData;
      });

      let w = maxW;
      let h = w / ratio;

      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }

      pdf.addImage(brandData, imgType, marginX, marginY, w, h);
    }

    const titleH = 0.8;
    const clientH = 0.6;
    const periodH = 0.45;
    const gap1 = 0.15;
    const gap2 = 0.10;

    const blockH = titleH + gap1 + clientH + gap2 + periodH;
    const baseY = (PAGE_H - blockH) / 2;

    pdf.setTextColor(coverTitleColor);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(40); 
    pdf.text(titleText, 0.9, baseY + 0.55);

    pdf.setTextColor(coverTitleColor);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text(clientName, 0.9, baseY + titleH + gap1 + 0.45);

    pdf.setTextColor(coverMetaColor);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(14);
    pdf.text(periodText, 0.9, baseY + titleH + gap1 + clientH + gap2 + 0.30);
  }

  const totalPages = 2 + pages.length;

  {
    pdf.addPage();
    addBackground();

    pdf.setTextColor(titleColor);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(28);
    pdf.text("Sumário", innerX + titleLeftOffset, innerY + 0.55);

    pdf.setTextColor("#6B7280");
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.text(`${clientName} • ${periodText}`, innerX + titleLeftOffset, innerY + 0.95);

    const items = pages.map((p, i) => {
      const t =
        (Array.isArray(titles) && titles[i]) ||
        p?.title ||
        (p.kind?.includes("kpis") ? "Resumo" : "Página");
      return {
        text: t,
        target: FIRST_CONTENT_PAGE_NUMBER + i,
      };
    });

    const startY = innerY + 1.25;
    const lineH = 0.38;

    const columns = Math.max(1, tocColumns);
    const colGap = 0.6;

    const colW = (innerW - titleLeftOffset - colGap * (columns - 1)) / columns;
    const colX0 = innerX + titleLeftOffset;

    const maxLinesPerCol = Math.floor((PAGE_H - startY - slidePadding - 0.6) / lineH);
    const itemsPerCol = Math.min(maxLinesPerCol, Math.ceil(items.length / columns));

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);

    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < itemsPerCol; row++) {
        const idx = col * itemsPerCol + row;
        if (idx >= items.length) break;

        const it = items[idx];
        const x = colX0 + col * (colW + colGap);
        const y = startY + row * lineH;

        pdf.setTextColor("#614a9e");
        pdf.text(it.text, x, y);

        const linkW = colW - 0.5;
        const linkH = lineH;
        pdf.link(x, y - linkH + 0.1, linkW, linkH, { pageNumber: it.target });

        pdf.setTextColor("#6B7280");
        pdf.text(String(it.target), x + colW, y, { align: "right" });
      }
    }

    addPagination(2, totalPages);
  }

  for (let i = 0; i < pages.length; i++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const p = pages[i];
    const pageNumber = FIRST_CONTENT_PAGE_NUMBER + i;

    pdf.addPage();
    addBackground();

    const pageTitle =
      (Array.isArray(titles) && titles[i]) ||
      p?.title ||
      (p.kind?.includes("kpis") ? "Resumo" : "Gráfico");

    setTitleStyle();
    const TITLE_TOP_OFFSET = 0.35; 
    pdf.text(pageTitle, innerX + titleLeftOffset, innerY + TITLE_TOP_OFFSET + 0.35);

    const imgAreaX = innerX + CONTENT_IMAGE_LEFT_INSET;
    const imgAreaY = innerY + titleBoxH + CONTENT_IMAGE_TOP_GAP;
    const imgAreaW = innerW - CONTENT_IMAGE_LEFT_INSET;
    const imgAreaH = innerH - titleBoxH - CONTENT_IMAGE_TOP_GAP - CONTENT_IMAGE_BOTTOM_GAP;

    const ratio = p.height / p.width;
    let drawW = imgAreaW;
    let drawH = drawW * ratio;

    if (drawH > imgAreaH) {
      drawH = imgAreaH;
      drawW = drawH / ratio;
    }

    const contentImageMaxScale = getContentImageMaxScale(p?.layoutType);
    const maxW = imgAreaW * contentImageMaxScale;
    const maxH = imgAreaH * contentImageMaxScale;

    if (drawW > maxW) {
      drawW = maxW;
      drawH = drawW * ratio;
    }
    if (drawH > maxH) {
      drawH = maxH;
      drawW = drawH / ratio;
    }

    const x = imgAreaX + (imgAreaW - drawW) / 2;
    const y = imgAreaY + (imgAreaH - drawH) / 2;

    pdf.addImage(p.data, p.type || "PNG", x, y, drawW, drawH);

    addPagination(pageNumber, totalPages);

    const pct = 90 + Math.round(((i + 1) / Math.max(1, pages.length)) * 9);
    report(Math.min(99, pct), `Montando página ${i + 1}/${pages.length}…`);
  }

  report(99, "Finalizando…");
  pdf.save(`${filename}.pdf`);
  report(100, "Concluído!");
}
